/**
 * Org URL segments: slug-first, id-tolerant.
 *
 * The first URL segment on org routes (`/$org/...`) is the organization's
 * slug — unique and `NOT NULL` in the Amoreg database — with the org/project
 * id accepted as a fallback so old links, the server-welcome bootstrap
 * navigation (which only knows the project id), and plain-T3 servers without
 * a tenant directory keep working.
 *
 * The id↔slug directory is a module-level snapshot fed by `state/orgs.ts`
 * when `/api/amoreg/orgs` answers. It lives here, not in `state/orgs.ts`,
 * so pure route helpers (`threadRoutes.ts`) can consume it without importing
 * React state (`state/orgs.ts` imports from `threadRoutes.ts`).
 */

export interface OrgDirectoryEntry {
  readonly id: string;
  readonly slug: string;
}

/**
 * First-segment path names owned by static routes. TanStack ranks static
 * segments above dynamic ones, so an org slugged like one of these would be
 * shadowed — such orgs keep id-based URLs instead.
 */
const RESERVED_ORG_SEGMENTS: ReadonlySet<string> = new Set([
  "connect",
  "connections",
  "pair",
  "settings",
  "usage",
]);

let orgDirectory: readonly OrgDirectoryEntry[] = [];

/**
 * Publish the latest orgs snapshot for URL building. Pass an empty array
 * when the server has no tenant directory — a stale mapping from a previous
 * server must not translate the next server's project-id segments.
 */
export function setOrgDirectory(entries: readonly OrgDirectoryEntry[]): void {
  orgDirectory = entries;
}

/**
 * A slug is usable in URLs when it is non-empty, not shadowed by a static
 * route, and not equal to any *other* org's id — ids win during resolution
 * (see below), so emitting such a slug would route the link to the id's
 * owner instead.
 */
function usableSlug(
  entry: OrgDirectoryEntry,
  directory: readonly OrgDirectoryEntry[],
): boolean {
  return (
    entry.slug.length > 0 &&
    !RESERVED_ORG_SEGMENTS.has(entry.slug) &&
    !directory.some((candidate) => candidate.id === entry.slug && candidate.id !== entry.id)
  );
}

/**
 * The URL segment for an org (or the org-workspace project sharing its id):
 * the slug when the directory knows it and it is usable, otherwise the id.
 */
export function orgUrlSegment(orgId: string): string {
  const entry = orgDirectory.find((candidate) => candidate.id === orgId);
  return entry && usableSlug(entry, orgDirectory) ? entry.slug : orgId;
}

/**
 * The org id behind a URL segment: id match first, then slug match, then the
 * segment as-is — on servers without a tenant directory the segment *is* the
 * project id, and before the directory loads passing it through lets id
 * URLs work immediately.
 *
 * Ids win over slugs deliberately: ids are server-assigned while slugs are
 * user-editable, so a slug chosen to equal another org's id must never
 * capture that org's id URLs.
 */
export function resolveOrgIdFromSegment(
  segment: string,
  directory: readonly OrgDirectoryEntry[] = orgDirectory,
): string {
  const byId = directory.find((candidate) => candidate.id === segment);
  if (byId) {
    return byId.id;
  }
  const bySlug = directory.find((candidate) => candidate.slug === segment);
  if (bySlug) {
    return bySlug.id;
  }
  return segment;
}

/**
 * A segment that is not an org at all but the stringified absence of one:
 * `/undefined/compliance/...` is what a link built from a missing value
 * looks like. Such a URL should land on the user's org, not on "not found".
 */
export function isPlaceholderOrgSegment(segment: string): boolean {
  const normalized = segment.trim().toLowerCase();
  return normalized === "" || normalized === "undefined" || normalized === "null";
}

/**
 * The sections that follow the org segment (`/$org/chat`, `/$org/workflows`).
 * One of these where the org belongs (`/chat`, `/chat/workflows`) is a link or
 * bookmark that lost its org, not an org: left alone, the section becomes the
 * "active org", every link the rail builds carries it, and every org-scoped
 * request is refused.
 */
const ORG_SECTION_SEGMENTS: ReadonlySet<string> = new Set([
  "agents",
  "apps",
  "automations",
  "chat",
  "command-center",
  "compliance",
  "connections",
  "draft",
  "inbox",
  "regulation",
  "settings",
  "skills",
  "spaces",
  "usage",
  "workflows",
]);

/**
 * Where a URL whose org segment is no org should go instead, or null when the
 * segment is an org (or could be one): a placeholder (`/undefined/x`) takes
 * the user's org in its place; a section (`/chat`, `/chat/workflows`) is put
 * after the user's org, or, with more path after it, gives way to it
 * (`/chat/workflows` → `/<org>/workflows`). An org whose id or slug is the
 * segment always wins.
 */
export function misplacedOrgPath(
  pathname: string,
  segment: string,
  directory: readonly OrgDirectoryEntry[],
  targetSegment: string,
): string | null {
  if (directory.some((entry) => entry.id === segment || entry.slug === segment)) return null;
  if (isPlaceholderOrgSegment(segment)) return replaceOrgSegmentInPath(pathname, segment, targetSegment);
  if (!ORG_SECTION_SEGMENTS.has(segment.trim().toLowerCase())) return null;
  const rest = pathname.split("/").slice(2).filter((part) => part !== "");
  if (rest.length === 0) return `/${encodeURIComponent(targetSegment)}/${encodeURIComponent(segment)}`;
  return replaceOrgSegmentInPath(pathname, segment, targetSegment);
}

/**
 * The canonical segment a URL should show for what it currently shows, or
 * null when it is already canonical (or cannot be improved yet). Used to
 * replace id URLs with slug URLs once the directory loads.
 */
export function canonicalOrgSegment(
  segment: string,
  directory: readonly OrgDirectoryEntry[] = orgDirectory,
): string | null {
  const byId = directory.find((candidate) => candidate.id === segment);
  if (byId && usableSlug(byId, directory)) {
    return byId.slug;
  }
  return null;
}

/**
 * A pathname with its first segment (the org segment) swapped for the
 * canonical one, or null when the pathname's first segment isn't the
 * expected current segment. Pure so the canonicalizer's rewrite — which
 * must preserve the child route, search and hash — is testable.
 */
export function replaceOrgSegmentInPath(
  pathname: string,
  currentSegment: string,
  canonicalSegment: string,
): string | null {
  const segments = pathname.split("/");
  const rawFirst = segments[1];
  if (rawFirst === undefined) {
    return null;
  }
  let decodedFirst: string;
  try {
    decodedFirst = decodeURIComponent(rawFirst);
  } catch {
    return null;
  }
  if (decodedFirst !== currentSegment) {
    return null;
  }
  segments[1] = encodeURIComponent(canonicalSegment);
  return segments.join("/");
}
